import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";

const ROLE_LABELS = {
  admin: "Administrador",
  supervisor: "Supervisor",
  operador: "Operador",
};

const ROLE_BADGES = {
  admin: {
    bg: "#dbeafe",
    color: "#1d4ed8",
    border: "#93c5fd",
  },
  supervisor: {
    bg: "#fef3c7",
    color: "#b45309",
    border: "#fcd34d",
  },
  operador: {
    bg: "#e2e8f0",
    color: "#334155",
    border: "#cbd5e1",
  },
};


const ROLE_DESCRIPTIONS = {
  admin: {
    title: "Administrador",
    summary: "Control completo dentro de su equipo.",
    permissions: [
      { ok: true, text: "Ver datos del dashboard" },
      { ok: true, text: "Generar reportes" },
      { ok: true, text: "Configurar alertas" },
      { ok: true, text: "Administrar usuarios" },
    ],
  },
  supervisor: {
    title: "Supervisor",
    summary: "Puede operar y revisar información, pero no administrar usuarios.",
    permissions: [
      { ok: true, text: "Ver datos del dashboard" },
      { ok: true, text: "Generar reportes" },
      { ok: true, text: "Configurar alertas" },
      { ok: false, text: "Administrar usuarios" },
    ],
  },
  operador: {
    title: "Operador",
    summary: "Acceso limitado a visualización de datos.",
    permissions: [
      { ok: true, text: "Ver datos del dashboard" },
      { ok: false, text: "Generar reportes" },
      { ok: false, text: "Configurar alertas" },
      { ok: false, text: "Administrar usuarios" },
    ],
  },
};

const ALL_PERMISSIONS = [
  { code: "view_data", label: "Ver datos" },
  { code: "manage_alarms", label: "Gestionar alertas" },
  { code: "generate_reports", label: "Generar reportes" },
  { code: "manage_users", label: "Gestionar usuarios" },
];

const EMPTY_NEW_USER = {
  name: "",
  username: "",
  password: "",
  role: "operador",
  permissions: ["view_data"],
};

function RoleInfoBox({ role }) {
  const info = ROLE_DESCRIPTIONS[role] || ROLE_DESCRIPTIONS.operador;
  const badge = ROLE_BADGES[role] || ROLE_BADGES.operador;

  return (
    <div style={styles.roleInfoBox}>
      <div style={styles.roleInfoHeader}>
        <span
          style={{
            ...styles.roleBadge,
            background: badge.bg,
            color: badge.color,
            borderColor: badge.border,
          }}
        >
          {info.title}
        </span>

        <span style={styles.roleInfoSummary}>{info.summary}</span>
      </div>

      <div style={styles.rolePermissionList}>
        {info.permissions.map((item) => (
          <div
            key={item.text}
            style={{
              ...styles.rolePermissionItem,
              ...(item.ok ? styles.rolePermissionAllowed : styles.rolePermissionDenied),
            }}
          >
            <span style={styles.rolePermissionIcon}>
              {item.ok ? "✓" : "×"}
            </span>
            <span>{item.text}</span>
          </div>
        ))}
      </div>

      <div style={styles.roleInfoNote}>
        Los permisos se asignan automáticamente según el rol. El plan de la cuenta sigue aplicando límites como cantidad de sensores o días máximos de reporte.
      </div>
    </div>
  );
}

export default function AdminUsuarios() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const canManageUsers = !!user?.permissions?.includes("manage_users");
  const part11Enabled = !!user?.part11_enabled;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageMsg, setPageMsg] = useState("");
  const [pageMsgType, setPageMsgType] = useState("info");
  const [savingMap, setSavingMap] = useState({});
  const [deletingMap, setDeletingMap] = useState({});
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionMode, setActionMode] = useState(""); // "save" | "deactivate"
  const [actionTargetUser, setActionTargetUser] = useState(null);
  const [actionReason, setActionReason] = useState("");
  const [actionSignaturePassword, setActionSignaturePassword] = useState("");
  const [actionSignatureMeaning, setActionSignatureMeaning] = useState("Aprobación");
  const [sensorModalOpen, setSensorModalOpen] = useState(false);
  const [sensorTargetUser, setSensorTargetUser] = useState(null);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [sensorSaving, setSensorSaving] = useState(false);
  const [sensorList, setSensorList] = useState([]);  
  const [sensorSearch, setSensorSearch] = useState("");
  const [sensorTypeFilter, setSensorTypeFilter] = useState("Todos");

  const loadUsers = async () => {
    if (!canManageUsers) return;

    setLoading(true);
    setPageMsg("");
    setPageMsgType("info");

    try {
      const res = await fetch("/api/sensores/admin/team-users", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "No se pudo cargar la lista de usuarios");
      }

      const normalized = Array.isArray(data)
        ? data.map((u) => ({
            ...u,
            permissions: Array.isArray(u.permissions) ? u.permissions : [],
          }))
        : [];

      setUsers(normalized);
    } catch (err) {
      setPageMsg(err.message || "Error cargando usuarios");
      setPageMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [canManageUsers, token]);

  const handleRoleChange = (userId, newRole) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
    );
  };

  const handlePermissionToggle = (userId, permissionCode) => {
    setUsers((prev) =>
      prev.map((u) => {
        if (u.id !== userId) return u;

        const hasPermission = u.permissions.includes(permissionCode);
        const nextPermissions = hasPermission
          ? u.permissions.filter((p) => p !== permissionCode)
          : [...u.permissions, permissionCode];

        return { ...u, permissions: nextPermissions };
      })
    );
  };

const openSaveModal = (targetUser) => {
  setActionMode("save");
  setActionTargetUser(targetUser);
  setActionReason("");
  setActionSignaturePassword("");
  setActionSignatureMeaning("Aprobación");
  setActionModalOpen(true);
};

const openDeactivateModal = (targetUser) => {
  setActionMode("deactivate");
  setActionTargetUser(targetUser);
  setActionReason("");
  setActionSignaturePassword("");
  setActionSignatureMeaning("Aprobación");
  setActionModalOpen(true);
};

const closeActionModal = () => {
  setActionModalOpen(false);
  setActionMode("");
  setActionTargetUser(null);
  setActionReason("");
  setActionSignaturePassword("");
  setActionSignatureMeaning("Aprobación");
  setSearch("");
};

const handleSaveUser = async (
  targetUser,
  reasonParam,
  signaturePasswordParam,
  signatureMeaningParam
) => {
  const reason = String(reasonParam || "").trim();
  const signaturePassword = String(signaturePasswordParam || "").trim();
  const signatureMeaning = String(signatureMeaningParam || "").trim();

  if (!reason || reason.length < 5) {
    setPageMsg("Debes ingresar un motivo válido para guardar cambios.");
    setPageMsgType("error");
    return;
  }

if (part11Enabled && !signaturePassword) {
  setPageMsg("Debes ingresar tu contraseña para firmar los cambios.");
  setPageMsgType("error");
  return;
}

if (part11Enabled && (!signatureMeaning || signatureMeaning.length < 3)) {
  setPageMsg("Debes seleccionar el significado de la firma.");
  setPageMsgType("error");
  return;
}

  setSavingMap((prev) => ({ ...prev, [targetUser.id]: true }));
  setPageMsg("");
  setPageMsgType("info");

  try {
    const res = await fetch(`/api/sensores/admin/team-users/${targetUser.id}`, {
      method: "PUT",
      headers: authHeader,
      body: JSON.stringify({
        role: targetUser.role,
        permissions: targetUser.permissions,
        reason,
        signature_password: signaturePassword,
        signature_meaning: signatureMeaning,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.error || "No se pudo guardar el usuario");
    }

    setUsers((prev) =>
      prev.map((u) => (u.id === targetUser.id ? { ...u, ...data.user } : u))
    );

    setPageMsg(`Usuario ${targetUser.username} actualizado correctamente`);
    setPageMsgType("success");
    closeActionModal();
  } catch (err) {
    setPageMsg(err.message || "Error guardando cambios");
    setPageMsgType("error");
  } finally {
    setSavingMap((prev) => ({ ...prev, [targetUser.id]: false }));
  }
};


const handleDeactivateUser = async (
  targetUser,
  reasonParam,
  signaturePasswordParam,
  signatureMeaningParam
) => {
  const reason = String(reasonParam || "").trim();
  const signaturePassword = String(signaturePasswordParam || "").trim();
  const signatureMeaning = String(signatureMeaningParam || "").trim();

  if (!reason || reason.length < 5) {
    setPageMsg("Debes ingresar un motivo válido para desactivar el usuario.");
    setPageMsgType("error");
    return;
  }

if (part11Enabled && !signaturePassword) {
  setPageMsg("Debes ingresar tu contraseña para firmar la desactivación.");
  setPageMsgType("error");
  return;
}

if (part11Enabled && (!signatureMeaning || signatureMeaning.length < 3)) {
  setPageMsg("Debes seleccionar el significado de la firma.");
  setPageMsgType("error");
  return;
}

  setDeletingMap((prev) => ({ ...prev, [targetUser.id]: true }));
  setPageMsg("");
  setPageMsgType("info");

  try {
    const res = await fetch(`/api/sensores/admin/team-users/${targetUser.id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        reason,
        signature_password: signaturePassword,
        signature_meaning: signatureMeaning,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.error || "No se pudo desactivar el usuario");
    }

    setUsers((prev) => prev.filter((u) => u.id !== targetUser.id));
    setPageMsg(data?.message || `Usuario ${targetUser.username} desactivado correctamente`);
    setPageMsgType("success");
    closeActionModal();
  } catch (err) {
    setPageMsg(err.message || "Error desactivando usuario");
    setPageMsgType("error");
  } finally {
    setDeletingMap((prev) => ({ ...prev, [targetUser.id]: false }));
  }
};

const openSensorModal = async (targetUser) => {
  setSensorTargetUser(targetUser);
  setSensorModalOpen(true);
  setSensorLoading(true);
  setSensorSaving(false);
  setSensorList([]);
  setSensorSearch("");
  setSensorTypeFilter("Todos");
  setPageMsg("");
  setPageMsgType("info");

  try {
    const res = await fetch(`/api/sensores/admin/team-users/${targetUser.id}/sensors`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.error || "No se pudo cargar la lista de sensores");
    }

    setSensorList(Array.isArray(data?.sensors) ? data.sensors : []);
  } catch (err) {
    setPageMsg(err.message || "Error cargando sensores");
    setPageMsgType("error");
    setSensorModalOpen(false);
    setSensorTargetUser(null);
  } finally {
    setSensorLoading(false);
  }
};


const closeSensorModal = () => {
  setSensorModalOpen(false);
  setSensorTargetUser(null);
  setSensorList([]);
  setSensorLoading(false);
  setSensorSaving(false);
  setSensorSearch("");
  setSensorTypeFilter("Todos");
};


const handleSensorToggle = (deviceId) => {
  setSensorList((prev) =>
    prev.map((s) =>
      s.device_id === deviceId ? { ...s, assigned: !s.assigned } : s
    )
  );
};


const handleSelectVisibleSensors = () => {
  const visibleIds = new Set(visibleSensors.map((s) => s.device_id));

  setSensorList((prev) =>
    prev.map((s) =>
      visibleIds.has(s.device_id) ? { ...s, assigned: true } : s
    )
  );
};

const handleClearVisibleSensors = () => {
  const visibleIds = new Set(visibleSensors.map((s) => s.device_id));

  setSensorList((prev) =>
    prev.map((s) =>
      visibleIds.has(s.device_id) ? { ...s, assigned: false } : s
    )
  );
};

const handleSaveSensors = async () => {
  if (!sensorTargetUser) return;

  const deviceIds = sensorList
    .filter((s) => s.assigned)
    .map((s) => s.device_id);

  setSensorSaving(true);
  setPageMsg("");
  setPageMsgType("info");

  try {
    const res = await fetch(`/api/sensores/admin/team-users/${sensorTargetUser.id}/sensors`, {
      method: "PUT",
      headers: authHeader,
      body: JSON.stringify({
        device_ids: deviceIds,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.error || "No se pudieron guardar los sensores");
    }

    setPageMsg(data?.message || `Sensores asignados correctamente a ${sensorTargetUser.username}`);
    setPageMsgType("success");
    closeSensorModal();
  } catch (err) {
    setPageMsg(err.message || "Error guardando sensores");
    setPageMsgType("error");
  } finally {
    setSensorSaving(false);
  }
};

const sensorTypeOptions = useMemo(() => {
  const types = Array.from(
    new Set(
      sensorList
        .map((s) => String(s.type || "").trim())
        .filter(Boolean)
    )
  ).sort();

  return ["Todos", ...types];
}, [sensorList]);

const visibleSensors = useMemo(() => {
  const q = sensorSearch.trim().toLowerCase();

  let base = [...sensorList];

  if (sensorTypeFilter !== "Todos") {
    base = base.filter((s) => String(s.type || "").trim() === sensorTypeFilter);
  }

  if (q) {
    base = base.filter((s) => {
      const byName = String(s.name || "").toLowerCase().includes(q);
      const byId = String(s.device_id || "").toLowerCase().includes(q);
      return byName || byId;
    });
  }

  base.sort((a, b) => {
 if (Boolean(b.assigned) !== Boolean(a.assigned)) {
      return Number(b.assigned) - Number(a.assigned);
    }
    if (Boolean(b.active) !== Boolean(a.active)) {
      return Number(b.active) - Number(a.active);
    }
    return String(a.name || a.device_id).localeCompare(String(b.name || b.device_id));
  });

  return base;
}, [sensorList, sensorSearch, sensorTypeFilter]);

const selectedSensorCount = useMemo(() => {
  return sensorList.filter((s) => s.assigned).length;
}, [sensorList]);


const visibleSensorCount = useMemo(() => visibleSensors.length, [visibleSensors]);

const handleNewUserField = (field, value) => {
  setNewUser((prev) => ({
    ...prev,
    [field]: value,
  }));
};


const handleNewUserPermissionToggle = (permissionCode) => {
  setNewUser((prev) => {
    const hasPermission = prev.permissions.includes(permissionCode);
    const nextPermissions = hasPermission
      ? prev.permissions.filter((p) => p !== permissionCode)
      : [...prev.permissions, permissionCode];

    return {
      ...prev,
      permissions: nextPermissions,
    };
  });
};

const handleNewUserRoleChange = (roleValue) => {
    setNewUser((prev) => {
      let nextPermissions = [...prev.permissions];

      if (roleValue === "admin" && !nextPermissions.includes("manage_users")) {
        nextPermissions.push("manage_users");
      }

      return {
        ...prev,
        role: roleValue,
        permissions: [...new Set(nextPermissions)],
      };
    });
  };

  const resetCreateForm = () => {
    setNewUser(EMPTY_NEW_USER);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    resetCreateForm();
  };

  const handleCreateUser = async () => {
    setPageMsg("");
    setPageMsgType("info");

    if (!newUser.name.trim()) {
      setPageMsg("El nombre es obligatorio");
      setPageMsgType("error");
      return;
    }

    if (!newUser.username.trim()) {
      setPageMsg("El usuario es obligatorio");
      setPageMsgType("error");
      return;
    }

    if (!newUser.password.trim() || newUser.password.trim().length < 6) {
      setPageMsg("La contraseña debe tener al menos 6 caracteres");
      setPageMsgType("error");
      return;
    }

    if (newUser.role === "admin" && !newUser.permissions.includes("manage_users")) {
      setPageMsg("Un administrador debe tener el permiso gestionar usuarios");
      setPageMsgType("error");
      return;
    }

    setCreating(true);

    try {
      const res = await fetch("/api/sensores/admin/team-users", {
        method: "POST",
        headers: authHeader,
        body: JSON.stringify({
          name: newUser.name.trim(),
          username: newUser.username.trim().toLowerCase(),
          password: newUser.password.trim(),
          role: newUser.role,
          permissions: newUser.permissions,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "No se pudo crear el usuario");
      }

      setUsers((prev) => [...prev, data.user].sort((a, b) => a.username.localeCompare(b.username)));
      setPageMsg(`Usuario ${data.user?.username || "nuevo"} creado correctamente`);
      setPageMsgType("success");
      closeCreateModal();
    } catch (err) {
      setPageMsg(err.message || "Error creando usuario");
      setPageMsgType("error");
    } finally {
      setCreating(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;

    return users.filter((u) => {
      const byName = String(u.name || "").toLowerCase().includes(q);
      const byUsername = String(u.username || "").toLowerCase().includes(q);
      return byName || byUsername;
    });
  }, [users, search]);

  if (!canManageUsers) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.title}>Acceso no autorizado</h2>
          <p style={styles.text}>
            Tu usuario no tiene permisos para administrar usuarios.
          </p>
          <button style={styles.primaryBtn} onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.mainTitle}>Administrar usuarios</h1>
            <p style={styles.subtitle}>
              Equipo: <strong>{user?.team || "Sin equipo"}</strong>
            </p>
          </div>

          <div style={styles.headerActions}>
            <button style={styles.successBtn} onClick={() => setCreateOpen(true)}>
              + Nuevo usuario
            </button>

            <button style={styles.secondaryBtn} onClick={() => navigate(-1)}>
              Volver
                              </button>
          </div>
        </div>

        <div style={styles.topControls}>
          <input
          type="text"
          placeholder="Buscar por nombre o usuario..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
          autoComplete="off"
          name="search_users_unique"
          />

          <div style={styles.counterBadge}>
            {filteredUsers.length} usuario{filteredUsers.length === 1 ? "" : "s"}
          </div>
        </div>

        {pageMsg ? (
          <div
            style={{
              ...styles.message,
              ...(pageMsgType === "success"
                ? styles.messageSuccess
                : pageMsgType === "error"
                ? styles.messageError
                : styles.messageInfo),
            }}
          >
            {pageMsg}
          </div>
        ) : null}

        {loading ? (
          <div style={styles.card}>
            <p style={styles.text}>Cargando usuarios...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={styles.card}>
            <p style={styles.text}>
              {search.trim()
                ? "No se encontraron usuarios con esa búsqueda."
                : "No hay usuarios en este equipo."}
            </p>
          </div>
        ) : (
          <div style={styles.list}>
            {filteredUsers.map((u) => {
              const isCurrentUser = Number(u.id) === Number(user?.id);
              const badge = ROLE_BADGES[u.role] || ROLE_BADGES.operador;

              return (
                <div
                  key={u.id}
                  style={{
                    ...styles.userCard,
                    ...(isCurrentUser ? styles.userCardCurrent : {}),
                  }}
                >
                  <div style={styles.userTop}>
                    <div style={styles.userIdentity}>
                      <div style={styles.userNameRow}>
                        <div style={styles.userName}>{u.name || u.username}</div>

                        <span
                          style={{
                            ...styles.roleBadge,
                            background: badge.bg,
                            color: badge.color,
                            borderColor: badge.border,
                          }}
                        >
                          {ROLE_LABELS[u.role] || u.role}
                        </span>

                        {isCurrentUser && (
                          <span style={styles.meBadge}>Tu usuario</span>
                        )}
                      </div>

                      <div style={styles.userMeta}>
                        @{u.username} · {u.team || "Sin equipo"}
                      </div>
                    </div>

                    <div style={styles.roleWrap}>
                      <label style={styles.label}>Rol</label>
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        style={styles.select}
                      >
                        <option value="admin">Administrador</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="operador">Operador</option>
                      </select>
                    </div>
                  </div>
                  <RoleInfoBox role={u.role} />

<div style={styles.autoPermissionNotice}>
  Los permisos de este usuario se asignarán automáticamente según el rol seleccionado.
</div>

<div style={styles.footer}>
  <span style={styles.currentRole}>
    Rol actual: <strong>{ROLE_LABELS[u.role] || u.role}</strong>
  </span>

<div style={styles.actionButtons}>
  {!isCurrentUser && (
    <>
      <button
        style={styles.secondaryBtn}
        onClick={() => openSensorModal(u)}
      >
        Asignar sensores
      </button>

      <button
        style={{
          ...styles.dangerBtn,
          ...(deletingMap[u.id] ? styles.primaryBtnDisabled : {}),
        }}
        onClick={() => openDeactivateModal(u)}
        disabled={!!deletingMap[u.id]}
      >
        {deletingMap[u.id] ? "Desactivando..." : "Desactivar"}
      </button>
    </>
  )}

  <button
    style={{
      ...styles.primaryBtn,
      ...(savingMap[u.id] ? styles.primaryBtnDisabled : {}),
    }}
    onClick={() => openSaveModal(u)}
    disabled={!!savingMap[u.id]}
  >
    {savingMap[u.id] ? "Guardando..." : "Guardar cambios"}
  </button>
</div>

</div>


              </div>
              );
            })}
          </div>
        )}
      </div>

{sensorModalOpen && sensorTargetUser && (
  <div style={styles.modalOverlay} onClick={closeSensorModal}>
    <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
      <div style={styles.modalHeader}>
        <div>
          <h2 style={styles.modalTitle}>Asignar sensores</h2>
          <p style={styles.modalSubtitle}>
            Usuario: <strong>{sensorTargetUser.name || sensorTargetUser.username}</strong>
          </p>
        </div>

        <button style={styles.secondaryBtn} onClick={closeSensorModal}>
          Cerrar
        </button>
      </div>

<div style={styles.modalBody}>
  <div style={styles.actionWarning}>
    Selecciona los sensores que este usuario podrá visualizar dentro del sistema.
  </div>

  <div style={styles.sensorTopBar}>
    <input
      type="text"
      value={sensorSearch}
      onChange={(e) => setSensorSearch(e.target.value)}
      placeholder="Buscar por nombre o device_id..."
      style={styles.searchInput}
      autoComplete="off"
      name="sensor_search_unique"
    />

    <select
      value={sensorTypeFilter}
      onChange={(e) => setSensorTypeFilter(e.target.value)}
      style={styles.select}
    >
      {sensorTypeOptions.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>

<div style={styles.sensorSummaryRow}>
  <div style={styles.sensorSummaryLeft}>
    <div style={styles.counterBadge}>
      {selectedSensorCount} seleccionado{selectedSensorCount === 1 ? "" : "s"}
    </div>

    <div style={styles.sensorResultsText}>
      {visibleSensorCount} resultado{visibleSensorCount === 1 ? "" : "s"} visible{visibleSensorCount === 1 ? "" : "s"}
    </div>
  </div>

  <div style={styles.sensorSummaryActions}>
    <button
      type="button"
      style={styles.secondaryBtn}
      onClick={handleSelectVisibleSensors}
      disabled={sensorLoading || sensorSaving || visibleSensorCount === 0}
    >
      Seleccionar visibles
    </button>

    <button
      type="button"
      style={styles.secondaryBtn}
      onClick={handleClearVisibleSensors}
      disabled={sensorLoading || sensorSaving || visibleSensorCount === 0}
    >
      Limpiar visibles
    </button>
  </div>
</div>

<div style={styles.sensorMiniLegend}>
  <span style={styles.sensorLegendItem}>✅ Asignados primero</span>
  <span style={styles.sensorLegendItem}>• Activos antes que inactivos</span>
</div>

  {sensorLoading ? (
    <div style={styles.card}>
      <p style={styles.text}>Cargando sensores...</p>
    </div>
  ) : visibleSensors.length === 0 ? (
    <div style={styles.card}>
      <p style={styles.text}>
        {sensorSearch.trim() || sensorTypeFilter !== "Todos"
          ? "No hay sensores que coincidan con el filtro actual."
          : "No hay sensores disponibles para asignar."}
      </p>
    </div>
  ) : (
    <div style={styles.sensorGrid}>
      {visibleSensors.map((sensor) => (
        <label
          key={sensor.device_id}
          style={{
            ...styles.sensorItem,
            ...(sensor.assigned ? styles.sensorItemAssigned : {}),
            ...(!sensor.active ? styles.sensorItemInactive : {}),
          }}
        >
          <input
            type="checkbox"
            checked={!!sensor.assigned}
            onChange={() => handleSensorToggle(sensor.device_id)}
          />

          <div style={styles.sensorTextWrap}>
            <div style={styles.sensorHeaderRow}>
              <div style={styles.sensorName}>{sensor.name || sensor.device_id}</div>

              <div style={styles.sensorBadges}>
                <span style={styles.sensorTypeBadge}>
                  {sensor.type || "Sin tipo"}
                </span>

                <span
                  style={{
                    ...styles.sensorStatusBadge,
                    ...(sensor.active ? styles.sensorStatusActive : styles.sensorStatusInactive),
                  }}
                >
                  {sensor.active ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>

            <div style={styles.sensorMeta}>
              {sensor.device_id}
            </div>
          </div>
        </label>
      ))}
    </div>
  )}
</div>


      <div style={styles.modalFooter}>
        <button style={styles.secondaryBtn} onClick={closeSensorModal}>
          Cancelar
        </button>

        <button
          style={{
            ...styles.primaryBtn,
            ...(sensorSaving ? styles.primaryBtnDisabled : {}),
          }}
          onClick={handleSaveSensors}
          disabled={sensorSaving || sensorLoading}
        >
          {sensorSaving ? "Guardando..." : "Guardar asignación"}
        </button>
      </div>
    </div>
  </div>
)}

{actionModalOpen && actionTargetUser && (
  <div style={styles.modalOverlay} onClick={closeActionModal}>
    <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
      <div style={styles.modalHeader}>
        <div>
          <h2 style={styles.modalTitle}>
            {actionMode === "deactivate" ? "Desactivar usuario" : "Guardar cambios"}
          </h2>
          <p style={styles.modalSubtitle}>
            Usuario: <strong>{actionTargetUser.name || actionTargetUser.username}</strong>
          </p>
        </div>

        <button style={styles.secondaryBtn} onClick={closeActionModal}>
          Cerrar
        </button>
      </div>

      <div style={styles.modalBody}>
        <div style={styles.actionWarning}>
          {actionMode === "deactivate"
            ? `Vas a desactivar a @${actionTargetUser.username}. Esta acción afectará su acceso al sistema.`
            : `Vas a guardar cambios de rol o permisos para @${actionTargetUser.username}.`}
        </div>

        <div style={styles.inputWrap}>
          <label style={styles.label}>Motivo del cambio</label>
          <textarea
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            style={styles.textarea}
            placeholder="Describe el motivo del cambio..."
            rows={4}
          />
        </div>

{part11Enabled && (
  <>
    <div style={styles.inputWrap}>
      <label style={styles.label}>Significado de la firma</label>
      <select
        value={actionSignatureMeaning}
        onChange={(e) => setActionSignatureMeaning(e.target.value)}
        style={styles.select}
      >
        <option value="Aprobación">Aprobación</option>
        <option value="Revisión">Revisión</option>
        <option value="Autorización">Autorización</option>
      </select>
    </div>

    <div style={styles.inputWrap}>
      <label style={styles.label}>Contraseña de firma</label>
      <input
        type="password"
        value={actionSignaturePassword}
        onChange={(e) => setActionSignaturePassword(e.target.value)}
        style={styles.searchInput}
        placeholder="Ingresa tu contraseña actual"
        autoComplete="new-password"
        name="signature_password_unique"
      />
    </div>
  </>
)}
      </div>

      <div style={styles.modalFooter}>
        <button style={styles.secondaryBtn} onClick={closeActionModal}>
          Cancelar
        </button>

        {actionMode === "deactivate" ? (
          <button
            style={{
              ...styles.dangerBtn,
              ...(deletingMap[actionTargetUser.id] ? styles.primaryBtnDisabled : {}),
            }}
          onClick={() =>
          handleDeactivateUser(
          actionTargetUser,
          actionReason,
          actionSignaturePassword,
          actionSignatureMeaning
          )
          }
            disabled={!!deletingMap[actionTargetUser.id]}
          >
            {deletingMap[actionTargetUser.id] ? "Desactivando..." : "Confirmar desactivación"}
          </button>
        ) : (
          <button
            style={{
              ...styles.primaryBtn,
              ...(savingMap[actionTargetUser.id] ? styles.primaryBtnDisabled : {}),
            }}
         onClick={() =>
         handleSaveUser(
         actionTargetUser,
         actionReason,
         actionSignaturePassword,
         actionSignatureMeaning
        )
        }
            disabled={!!savingMap[actionTargetUser.id]}
          >
            {savingMap[actionTargetUser.id] ? "Guardando..." : "Confirmar cambios"}
          </button>
        )}
      </div>
    </div>
  </div>
)}
     
 {createOpen && (
        <div style={styles.modalOverlay} onClick={closeCreateModal}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Nuevo usuario</h2>
                <p style={styles.modalSubtitle}>
                  Se creará dentro del equipo <strong>{user?.team || "Sin equipo"}</strong>
                </p>
              </div>

              <button style={styles.secondaryBtn} onClick={closeCreateModal}>
                Cerrar
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGrid}>
                <div style={styles.inputWrap}>
                  <label style={styles.label}>Nombre</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => handleNewUserField("name", e.target.value)}
                    style={styles.searchInput}
                    placeholder="Ej: Juan Pérez"
                  />
                </div>

                <div style={styles.inputWrap}>
                  <label style={styles.label}>Usuario</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => handleNewUserField("username", e.target.value)}
                    style={styles.searchInput}
                    placeholder="Ej: juanperez"
                  />
                </div>

                <div style={styles.inputWrap}>
                  <label style={styles.label}>Contraseña inicial</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => handleNewUserField("password", e.target.value)}
                    style={styles.searchInput}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>

                <div style={styles.inputWrap}>
                  <label style={styles.label}>Rol</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => handleNewUserRoleChange(e.target.value)}
                    style={styles.select}
                  >
                    <option value="admin">Administrador</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="operador">Operador</option>
                  </select>
                </div>
              </div>
              <RoleInfoBox role={newUser.role} />

              <div style={styles.autoPermissionNotice}>
              Los permisos de este usuario se asignarán automáticamente según el rol seleccionado.
              </div>

              </div>

            <div style={styles.modalFooter}>
              <button style={styles.secondaryBtn} onClick={closeCreateModal}>
                Cancelar
              </button>

              <button
                style={{
                  ...styles.successBtn,
                  ...(creating ? styles.primaryBtnDisabled : {}),
                }}
                onClick={handleCreateUser}
                disabled={creating}
              >
                {creating ? "Creando..." : "Crear usuario"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#e9f6fb",
    padding: "32px 16px",
  },
  container: {
    maxWidth: 1180,
    margin: "0 auto",
    display: "grid",
    gap: 18,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  topControls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  mainTitle: {
    margin: 0,
    color: "#0284c7",
    fontSize: "clamp(28px, 3vw, 40px)",
    fontWeight: 900,
  },
  subtitle: {
    margin: "6px 0 0 0",
    color: "#475569",
    fontSize: 16,
    fontWeight: 700,
  },
  card: {
    background: "#fff",
    border: "1px solid #dbeafe",
    borderRadius: 18,
    padding: 20,
    boxShadow: "0 8px 20px rgba(2,6,23,.06)",
  },
  list: {
    display: "grid",
    gap: 16,
  },
  userCard: {
    background: "#fff",
    border: "1px solid #dbeafe",
    borderRadius: 18,
    padding: 20,
    boxShadow: "0 8px 20px rgba(2,6,23,.06)",
    display: "grid",
    gap: 16,
  },
  userCardCurrent: {
    border: "2px solid #60a5fa",
    boxShadow: "0 12px 26px rgba(37,99,235,.10)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  },
  userTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  userIdentity: {
    display: "grid",
    gap: 6,
  },
  userNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  userName: {
    fontSize: 22,
    fontWeight: 900,
    color: "#0f172a",
  },
  userMeta: {
    color: "#64748b",
    fontWeight: 700,
  },
  roleWrap: {
    minWidth: 240,
    display: "grid",
    gap: 6,
  },
  inputWrap: {
    display: "grid",
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 800,
    color: "#475569",
  },
  select: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "10px 12px",
    fontWeight: 700,
    fontSize: 15,
    background: "#fff",
  },
  permissionsBlock: {
    display: "grid",
    gap: 10,
  },
  permissionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
  },
  checkboxItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 700,
    color: "#0f172a",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  currentRole: {
    color: "#475569",
    fontWeight: 700,
  },
  primaryBtn: {
    border: "none",
    borderRadius: 12,
    padding: "11px 18px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(37,99,235,.18)",
    transition: "transform .18s ease, opacity .18s ease",
  },
  successBtn: {
    border: "none",
    borderRadius: 12,
    padding: "11px 18px",
    background: "#16a34a",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(22,163,74,.18)",
  },
  primaryBtnDisabled: {
    opacity: 0.75,
    cursor: "not-allowed",
  },
  secondaryBtn: {
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: "11px 16px",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },
  searchInput: {
    flex: "1 1 320px",
    minWidth: 260,
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: "12px 14px",
    background: "#fff",
    fontWeight: 700,
    fontSize: 15,
    color: "#0f172a",
  },
  counterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 14px",
    borderRadius: 999,
    background: "#ffffff",
    border: "1px solid #dbeafe",
    color: "#0369a1",
    fontWeight: 800,
  },
  roleBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 10px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 12,
    fontWeight: 900,
  },
  meBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 10px",
    borderRadius: 999,
    background: "#dcfce7",
    color: "#166534",
    border: "1px solid #86efac",
    fontSize: 12,
    fontWeight: 900,
  },
  message: {
    borderRadius: 12,
    padding: "12px 14px",
    fontWeight: 800,
  },
  messageInfo: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
  },
  messageSuccess: {
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    color: "#047857",
  },
  messageError: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
  },
  text: {
    margin: 0,
    color: "#334155",
    fontWeight: 700,
  },
  title: {
    margin: "0 0 10px 0",
    color: "#0f172a",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 1000,
  },
  modalCard: {
    width: "min(760px, 96vw)",
    background: "#fff",
    borderRadius: 18,
    border: "1px solid #dbeafe",
    boxShadow: "0 24px 60px rgba(2,6,23,.22)",
    display: "grid",
    gap: 16,
    padding: 22,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
  },
  modalTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 900,
  },
  modalSubtitle: {
    margin: "6px 0 0 0",
    color: "#64748b",
    fontWeight: 700,
  },
  modalBody: {
    display: "grid",
    gap: 16,
  },
  
actionWarning: {
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  borderRadius: 12,
  padding: "12px 14px",
  fontWeight: 700,
  lineHeight: 1.5,
},

textarea: {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: "12px 14px",
  background: "#fff",
  fontWeight: 700,
  fontSize: 15,
  color: "#0f172a",
  resize: "vertical",
  fontFamily: "inherit",
  boxSizing: "border-box",
},

    formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
actionButtons: {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
},
dangerBtn: {
  border: "1px solid #fecaca",
  borderRadius: 12,
  padding: "10px 14px",
  background: "#fff1f2",
  color: "#b91c1c",
  fontWeight: 800,
  cursor: "pointer",
},
sensorGrid: {
  display: "grid",
  gap: 10,
  maxHeight: "420px",
  overflowY: "auto",
  paddingRight: 4,
},

sensorItem: {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: "12px 14px",
  cursor: "pointer",
},

sensorTextWrap: {
  display: "grid",
  gap: 4,
},

sensorName: {
  fontWeight: 800,
  color: "#0f172a",
},

sensorMeta: {
  fontSize: 13,
  color: "#64748b",
  fontWeight: 700,
},

sensorTopBar: {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 220px",
  gap: 12,
  alignItems: "center",
},

sensorSummaryRow: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
},

sensorMiniLegend: {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
},

sensorLegendItem: {
  fontSize: 13,
  fontWeight: 700,
  color: "#64748b",
},

sensorHeaderRow: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
  flexWrap: "wrap",
},

sensorBadges: {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
},

sensorTypeBadge: {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 10px",
  borderRadius: 999,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1px solid #bfdbfe",
  fontSize: 12,
  fontWeight: 800,
},

sensorStatusBadge: {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  border: "1px solid",
},

sensorStatusActive: {
  background: "#ecfdf5",
  color: "#047857",
  borderColor: "#a7f3d0",
},

sensorStatusInactive: {
  background: "#fef2f2",
  color: "#b91c1c",
  borderColor: "#fecaca",
},

sensorItemAssigned: {
  border: "2px solid #93c5fd",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  boxShadow: "0 10px 22px rgba(37,99,235,.08)",
},

sensorItemInactive: {
  opacity: 0.82,
},
sensorSummaryLeft: {
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
},

sensorResultsText: {
  fontSize: 13,
  fontWeight: 800,
  color: "#64748b",
},

sensorSummaryActions: {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
},
roleInfoBox: {
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  border: "1px solid #bfdbfe",
  borderRadius: 16,
  padding: 14,
  display: "grid",
  gap: 12,
},

roleInfoHeader: {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
},

roleInfoSummary: {
  color: "#475569",
  fontWeight: 800,
  fontSize: 13,
},

rolePermissionList: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: 8,
},

rolePermissionItem: {
  display: "flex",
  alignItems: "center",
  gap: 8,
  borderRadius: 12,
  padding: "9px 11px",
  fontWeight: 800,
  fontSize: 13,
  border: "1px solid",
},

rolePermissionAllowed: {
  background: "#ecfdf5",
  borderColor: "#a7f3d0",
  color: "#047857",
},

rolePermissionDenied: {
  background: "#f8fafc",
  borderColor: "#e2e8f0",
  color: "#64748b",
},

rolePermissionIcon: {
  width: 20,
  height: 20,
  borderRadius: 999,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#ffffff",
  fontWeight: 900,
},

roleInfoNote: {
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1e40af",
  borderRadius: 12,
  padding: "10px 12px",
  fontSize: 12,
  lineHeight: 1.5,
  fontWeight: 700,
},
autoPermissionNotice: {
  background: "#f8fafc",
  border: "1px dashed #bfdbfe",
  color: "#475569",
  borderRadius: 12,
  padding: "11px 13px",
  fontSize: 13,
  fontWeight: 800,
  lineHeight: 1.5,
},
};
